import React, { useEffect, useRef } from "react";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import LinkIcon from "@mui/icons-material/Link";
import FormatClearIcon from "@mui/icons-material/FormatClear";
import { isHtmlEmpty, looksLikeHtml, plainTextToHtml, sanitizeHtml } from "../utils/sanitizeHtml";

interface RichTextEditorProps {
  label?: string;
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  minHeight?: number;
}

export const richContentStyles = {
  "& p, & div": { margin: 0 },
  "& p": { marginBottom: "0.75em" },
  "& h1": { fontSize: "1.4rem", fontWeight: 700, margin: "0.5em 0" },
  "& h2": { fontSize: "1.2rem", fontWeight: 700, margin: "0.5em 0" },
  "& h3": { fontSize: "1.05rem", fontWeight: 700, margin: "0.5em 0" },
  "& ul, & ol": { margin: "0.25em 0 0.75em", paddingLeft: "1.5em" },
  "& blockquote": { margin: "0.5em 0", paddingLeft: "1em", borderLeft: "3px solid currentColor", opacity: 0.85 },
  "& a": { color: "inherit", textDecoration: "underline" },
} as const;

export default function RichTextEditor({
  label,
  value,
  onChange,
  placeholder,
  minHeight = 140,
}: RichTextEditorProps) {
  const ref = useRef<HTMLDivElement>(null);
  const lastEmitted = useRef<string | null>(null);

  const toEditorHtml = (v: string) =>
    v ? (looksLikeHtml(v) ? sanitizeHtml(v) : plainTextToHtml(v)) : "";

  // Sync external changes (e.g. opening a different tournament) without disturbing typing.
  useEffect(() => {
    if (!ref.current) return;
    if (value === lastEmitted.current) return;
    ref.current.innerHTML = toEditorHtml(value);
    lastEmitted.current = value;
  }, [value]);

  const emit = () => {
    if (!ref.current) return;
    const html = isHtmlEmpty(ref.current.innerHTML) ? "" : sanitizeHtml(ref.current.innerHTML);
    lastEmitted.current = html;
    onChange(html);
  };

  const exec = (command: string, arg?: string) => {
    ref.current?.focus();
    document.execCommand(command, false, arg);
    emit();
  };

  const addLink = () => {
    const url = window.prompt("Link URL (https://...)");
    if (url && /^(https?:|mailto:)/i.test(url.trim())) exec("createLink", url.trim());
  };

  const btn = (title: string, onClick: () => void, icon: React.ReactNode) => (
    <Tooltip title={title}>
      <IconButton size="small" onMouseDown={(e) => e.preventDefault()} onClick={onClick}>
        {icon}
      </IconButton>
    </Tooltip>
  );

  return (
    <Box>
      {label && (
        <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 0.5 }}>
          {label}
        </Typography>
      )}
      <Box
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          "&:focus-within": { borderColor: "primary.main" },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 0.25,
            p: 0.5,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          {btn("Bold", () => exec("bold"), <FormatBoldIcon fontSize="small" />)}
          {btn("Italic", () => exec("italic"), <FormatItalicIcon fontSize="small" />)}
          {btn("Underline", () => exec("underline"), <FormatUnderlinedIcon fontSize="small" />)}
          <Box
            component="select"
            defaultValue=""
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
              exec("formatBlock", e.target.value || "p");
              e.target.value = "";
            }}
            sx={{
              fontSize: "0.8rem",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 0.5,
              background: "transparent",
              color: "inherit",
              height: 26,
              mx: 0.5,
            }}
          >
            <option value="">Style</option>
            <option value="p">Paragraph</option>
            <option value="h1">Heading 1</option>
            <option value="h2">Heading 2</option>
            <option value="h3">Heading 3</option>
          </Box>
          {btn("Bullet list", () => exec("insertUnorderedList"), <FormatListBulletedIcon fontSize="small" />)}
          {btn("Numbered list", () => exec("insertOrderedList"), <FormatListNumberedIcon fontSize="small" />)}
          {btn("Link", addLink, <LinkIcon fontSize="small" />)}
          {btn("Clear formatting", () => exec("removeFormat"), <FormatClearIcon fontSize="small" />)}
        </Box>
        <Box
          ref={ref}
          contentEditable
          suppressContentEditableWarning
          role="textbox"
          aria-multiline="true"
          data-placeholder={placeholder}
          onInput={emit}
          onBlur={emit}
          onPaste={(e: React.ClipboardEvent) => {
            e.preventDefault();
            const html = e.clipboardData.getData("text/html");
            const text = e.clipboardData.getData("text/plain");
            document.execCommand("insertHTML", false, html ? sanitizeHtml(html) : plainTextToHtml(text));
            emit();
          }}
          sx={{
            minHeight,
            maxHeight: 320,
            overflowY: "auto",
            p: 1.5,
            outline: "none",
            lineHeight: 1.6,
            "&:empty::before": { content: "attr(data-placeholder)", opacity: 0.5, pointerEvents: "none" },
            ...richContentStyles,
          }}
        />
      </Box>
    </Box>
  );
}
